import { Link } from 'react-router-dom';
import { platforms, categories, articles, faqs, siteInfo } from '@/mocks/data';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import SEO from '@/components/feature/SEO';

export default function HomePage() {
  const latestArticles = articles.slice(0, 6);

  const schema = {
    '@context': 'https://schema.org',
    '@type': 'WebSite',
    name: siteInfo.name,
    url: 'https://adultvideoguide.cn',
    description: siteInfo.description,
    potentialAction: {
      '@type': 'SearchAction',
      target: 'https://adultvideoguide.cn/search?q={search_term_string}',
      'query-input': 'required name=search_term_string',
    },
  };

  const faqSchema = {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: faqs.map((faq) => ({
      '@type': 'Question',
      name: faq.question,
      acceptedAnswer: {
        '@type': 'Answer',
        text: faq.answer,
      },
    })),
  };

  return (
    <>
      <SEO
        title="成人短视频平台推荐与观看指南 - 首页"
        description="为18岁以上成年用户提供合规成人短视频平台推荐、内容分类解析、安全观看技巧及隐私保护建议。理性探索，安全观看。"
        keywords="成人短视频,成人短视频平台,成人短视频网站推荐,成人短视频合规观看,成人短视频隐私保护,成人短视频内容分类,18岁以上短视频"
        canonicalUrl="/"
        ogImage="https://readdy.ai/api/search-image?query=Elegant%20minimalist%20digital%20entertainment%20guide%20concept%20with%20warm%20burgundy%20and%20gold%20accents%2C%20clean%20modern%20aesthetic%2C%20abstract%20geometric%20shapes%20representing%20video%20content%2C%20soft%20neutral%20background%2C%20professional%20editorial%20style&width=1200&height=630&seq=home-og-01&orientation=landscape"
        schema={{ ...schema, ...faqSchema }}
      />

      <Navbar />

      <main>
        {/* Hero Section */}
        <section className="relative w-full h-[600px] md:h-[700px] flex items-center justify-center overflow-hidden">
          <img
            src="https://readdy.ai/api/search-image?query=Abstract%20minimalist%20digital%20entertainment%20landscape%20with%20warm%20dark%20red%20and%20amber%20gradients%2C%20soft%20flowing%20geometric%20shapes%2C%20elegant%20modern%20aesthetic%2C%20clean%20composition%2C%20no%20text%2C%20dark%20atmospheric%20background%20with%20subtle%20light%20accents%2C%20premium%20editorial%20style&width=1920&height=1080&seq=hero-bg-01&orientation=landscape"
            alt="成人短视频指南平台推荐与观看指南"
            className="absolute inset-0 w-full h-full object-cover object-top"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-black/50 via-black/35 to-black/55"></div>

          <div className="relative z-10 w-full px-4 md:px-6 text-center">
            <div className="max-w-4xl mx-auto">
              <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-background-50/15 backdrop-blur-sm border border-background-50/20 mb-6">
                <span className="w-2 h-2 rounded-full bg-accent-500 animate-pulse"></span>
                <span className="text-xs text-background-50/90 font-medium">仅供18岁以上成年人阅览</span>
              </div>
              <h1 className="text-3xl md:text-5xl lg:text-6xl font-heading font-bold text-background-50 leading-tight mb-4">
                成人短视频平台
                <br />
                <span className="text-accent-300">推荐与观看指南</span>
              </h1>
              <p className="text-base md:text-lg text-background-50/80 max-w-2xl mx-auto mb-8 leading-relaxed">
                为成年用户提供合规平台推荐、内容分类解析、安全观看技巧及隐私保护建议，
                帮助您理性、安全地探索成人短视频相关资源与服务。
              </p>
              <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
                <Link
                  to="/category/platform-recommend"
                  className="px-8 py-3 bg-primary-500 text-background-50 rounded-full font-medium text-sm hover:bg-primary-600 transition-colors whitespace-nowrap"
                >
                  <i className="ri-compass-3-line mr-2"></i>
                  查看平台推荐
                </Link>
                <Link
                  to="/category/safety-guide"
                  className="px-8 py-3 bg-background-50/10 backdrop-blur-sm text-background-50 border border-background-50/30 rounded-full font-medium text-sm hover:bg-background-50/20 transition-colors whitespace-nowrap"
                >
                  <i className="ri-shield-check-line mr-2"></i>
                  安全观看指南
                </Link>
              </div>
            </div>
          </div>

          <div className="absolute bottom-8 left-1/2 -translate-x-1/2 animate-bounce">
            <i className="ri-arrow-down-line text-background-50/60 text-2xl"></i>
          </div>
        </section>

        {/* Platform Recommendations */}
        <section className="w-full px-4 md:px-6 py-16 md:py-20 bg-background-50">
          <div className="max-w-7xl mx-auto">
            <div className="text-center mb-12">
              <h2 className="text-2xl md:text-3xl font-heading font-bold text-foreground-900 mb-3">
                精选平台推荐
              </h2>
              <p className="text-foreground-600 max-w-xl mx-auto text-sm md:text-base">
                基于安全性、内容质量和用户体验精选的合规成人短视频平台
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              {platforms.map((platform) => (
                <article
                  key={platform.id}
                  className="group bg-background-100 rounded-xl overflow-hidden border border-background-200/70 hover:border-primary-300 transition-all duration-300"
                >
                  <div className="relative h-48 overflow-hidden">
                    <img
                      src={platform.image}
                      alt={platform.name}
                      className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
                    />
                    <div className="absolute top-3 right-3 flex items-center gap-1 px-2.5 py-1 bg-background-50/90 backdrop-blur-sm rounded-full">
                      <i className="ri-star-fill text-accent-500 text-xs"></i>
                      <span className="text-xs font-semibold text-foreground-900">{platform.rating}</span>
                    </div>
                  </div>
                  <div className="p-5">
                    <h3 className="font-heading text-lg font-semibold text-foreground-900 mb-2">{platform.name}</h3>
                    <p className="text-sm text-foreground-600 leading-relaxed mb-4">{platform.description}</p>
                    <div className="flex flex-wrap gap-2">
                      {platform.features.map((feature) => (
                        <span key={feature} className="px-2.5 py-1 bg-secondary-100 text-secondary-800 text-xs rounded-full whitespace-nowrap">
                          {feature}
                        </span>
                      ))}
                    </div>
                  </div>
                </article>
              ))}
            </div>

            <div className="text-center mt-10">
              <Link
                to="/category/platform-recommend"
                className="inline-flex items-center gap-2 text-sm font-medium text-primary-600 hover:text-primary-700 transition-colors whitespace-nowrap"
              >
                查看全部平台推荐
                <i className="ri-arrow-right-line"></i>
              </Link>
            </div>
          </div>
        </section>

        {/* Category Navigation */}
        <section className="w-full px-4 md:px-6 py-16 md:py-20 bg-background-100">
          <div className="max-w-7xl mx-auto">
            <div className="text-center mb-12">
              <h2 className="text-2xl md:text-3xl font-heading font-bold text-foreground-900 mb-3">
                内容分类导航
              </h2>
              <p className="text-foreground-600 max-w-xl mx-auto text-sm md:text-base">
                按主题分类探索成人短视频相关内容，精准定位您感兴趣的方向
              </p>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
              {categories.map((cat) => (
                <Link
                  key={cat.slug}
                  to={`/category/${cat.slug}`}
                  className="group bg-background-50 rounded-xl p-6 border border-background-200/70 hover:border-primary-300 transition-all duration-300"
                >
                  <div className="w-10 h-10 flex items-center justify-center rounded-lg bg-primary-100 text-primary-600 mb-4 group-hover:bg-primary-500 group-hover:text-background-50 transition-colors">
                    <i className={`${cat.icon} text-lg`}></i>
                  </div>
                  <h3 className="font-heading text-base font-semibold text-foreground-900 mb-2">{cat.name}</h3>
                  <p className="text-sm text-foreground-600 leading-relaxed mb-3">{cat.description}</p>
                  <div className="flex items-center justify-between">
                    <span className="text-xs text-foreground-500">{cat.articleCount} 篇文章</span>
                    <span className="flex items-center gap-1 text-xs font-medium text-primary-600 group-hover:translate-x-1 transition-transform whitespace-nowrap">
                      浏览分类
                      <i className="ri-arrow-right-line"></i>
                    </span>
                  </div>
                </Link>
              ))}
            </div>
          </div>
        </section>

        {/* Latest Articles */}
        <section className="w-full px-4 md:px-6 py-16 md:py-20 bg-background-50">
          <div className="max-w-7xl mx-auto">
            <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between mb-12">
              <div>
                <h2 className="text-2xl md:text-3xl font-heading font-bold text-foreground-900 mb-2">
                  最新文章
                </h2>
                <p className="text-foreground-600 text-sm">精选优质内容，持续更新中</p>
              </div>
              <Link
                to="/category/platform-recommend"
                className="mt-3 sm:mt-0 inline-flex items-center gap-2 text-sm font-medium text-primary-600 hover:text-primary-700 transition-colors whitespace-nowrap"
              >
                查看全部文章
                <i className="ri-arrow-right-line"></i>
              </Link>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {latestArticles.map((article) => (
                <Link
                  key={article.slug}
                  to={`/article/${article.slug}`}
                  className="group bg-background-100 rounded-xl overflow-hidden border border-background-200/70 hover:border-primary-300 transition-all duration-300"
                >
                  <div className="relative h-44 overflow-hidden">
                    <img
                      src={article.coverImage}
                      alt={article.title}
                      className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
                    />
                    <div className="absolute bottom-3 left-3">
                      <span className="px-2.5 py-1 bg-background-50/90 backdrop-blur-sm text-xs text-foreground-700 rounded-full">
                        {article.readTime}
                      </span>
                    </div>
                  </div>
                  <div className="p-5">
                    <div className="flex items-center gap-3 text-xs text-foreground-500 mb-3">
                      <span>{article.date}</span>
                      <span className="w-1 h-1 rounded-full bg-foreground-400"></span>
                      <span>{article.author}</span>
                    </div>
                    <h3 className="font-heading text-base font-semibold text-foreground-900 mb-2 line-clamp-2 group-hover:text-primary-600 transition-colors">
                      {article.title}
                    </h3>
                    <p className="text-sm text-foreground-600 leading-relaxed line-clamp-2 mb-3">{article.excerpt}</p>
                    <div className="flex flex-wrap gap-2">
                      {article.tags.slice(0, 2).map((tag) => (
                        <span key={tag} className="px-2 py-0.5 bg-secondary-100 text-secondary-700 text-xs rounded whitespace-nowrap">
                          {tag}
                        </span>
                      ))}
                    </div>
                  </div>
                </Link>
              ))}
            </div>
          </div>
        </section>

        {/* FAQ Section */}
        <section className="w-full px-4 md:px-6 py-16 md:py-20 bg-background-100">
          <div className="max-w-4xl mx-auto">
            <div className="text-center mb-12">
              <h2 className="text-2xl md:text-3xl font-heading font-bold text-foreground-900 mb-3">
                常见问题
              </h2>
              <p className="text-foreground-600 text-sm md:text-base">
                关于成人短视频平台的常见疑问与解答
              </p>
            </div>

            <div className="space-y-3">
              {faqs.map((faq, index) => (
                <details key={index} className="group bg-background-50 rounded-xl border border-background-200/70">
                  <summary className="flex items-center justify-between p-5 cursor-pointer list-none">
                    <h3 className="text-sm md:text-base font-medium text-foreground-900 pr-8">{faq.question}</h3>
                    <i className="ri-add-line text-foreground-500 group-open:hidden flex-shrink-0"></i>
                    <i className="ri-subtract-line text-primary-500 hidden group-open:block flex-shrink-0"></i>
                  </summary>
                  <div className="px-5 pb-5">
                    <p className="text-sm text-foreground-600 leading-relaxed">{faq.answer}</p>
                  </div>
                </details>
              ))}
            </div>
          </div>
        </section>

        {/* CTA Section */}
        <section className="w-full px-4 md:px-6 py-16 md:py-20 bg-background-50">
          <div className="max-w-3xl mx-auto text-center">
            <div className="w-16 h-16 flex items-center justify-center rounded-2xl bg-primary-100 text-primary-600 mx-auto mb-6">
              <i className="ri-mail-send-line text-2xl"></i>
            </div>
            <h2 className="text-2xl md:text-3xl font-heading font-bold text-foreground-900 mb-3">
              有疑问或建议？
            </h2>
            <p className="text-foreground-600 text-sm md:text-base mb-8 max-w-lg mx-auto">
              如果您对我们的内容有任何疑问、建议或合作意向，欢迎随时与我们取得联系。
            </p>
            <Link
              to="/about"
              className="inline-flex items-center gap-2 px-8 py-3 bg-primary-500 text-background-50 rounded-full font-medium text-sm hover:bg-primary-600 transition-colors whitespace-nowrap"
            >
              <i className="ri-contacts-line"></i>
              联系我们
            </Link>
          </div>
        </section>
      </main>

      <Footer />
    </>
  );
}